import CrudPage, { Pill } from "@/components/CrudPage";

const CODECS = ["opus", "g722", "ulaw", "alaw", "g729"].map((c) => ({ value: c, label: c }));
const statusOpts = [{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }];
const transportOpts = [{ value: "udp", label: "UDP" }, { value: "tcp", label: "TCP" }, { value: "tls", label: "TLS" }];
const destTypeOpts = [
  { value: "extension", label: "Extension" }, { value: "ring_group", label: "Ring Group" },
  { value: "ivr", label: "IVR" }, { value: "queue", label: "Queue" },
  { value: "voicemail", label: "Voicemail" }, { value: "department", label: "Department" },
];

export function Departments() {
  return (
    <CrudPage
      title="Departments" testid="dept" addLabel="New Department"
      subtitle="Isolate teams into departments. Assign a SIP trunk and a main DID so inbound calls route straight to the right group."
      endpoint="/departments"
      columns={[
        { name: "name", label: "Name", render: (it) => (
          <span className="flex items-center gap-2 font-medium">
            <i className="w-3 h-3 rounded-full inline-block" style={{ background: it.color }} />{it.name}
          </span>) },
        { name: "description", label: "Description", render: (it) => <span className="text-muted-foreground">{it.description || "—"}</span> },
        { name: "main_did", label: "Main DID", render: (it) => <span className="font-mono">{it.main_did || "—"}</span> },
        { name: "trunk_id", label: "Trunk", render: (it, h) => h.labelForValue(h.fields.find((f) => f.name === "trunk_id"), it.trunk_id) },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "color", label: "Colour", type: "color", default: "#D4AF37" },
        { name: "description", label: "Description", type: "textarea", full: true },
        { name: "trunk_id", label: "Assigned SIP Trunk", type: "select", optionsEndpoint: "/trunks" },
        { name: "main_did", label: "Main DID / DDI" },
      ]}
    />
  );
}

export function Trunks() {
  return (
    <CrudPage
      title="SIP Trunks" testid="trunk" addLabel="New Trunk"
      subtitle="Connect your VoIP provider. Trunks carry inbound DIDs and outbound calls to the PSTN."
      endpoint="/trunks"
      columns={[
        { name: "name", label: "Name", render: (it) => <span className="font-medium">{it.name}</span> },
        { name: "provider", label: "Provider" },
        { name: "host", label: "Host", render: (it) => <span className="font-mono">{it.host}:{it.port}</span> },
        { name: "transport", label: "Transport", render: (it) => <Pill tone="blue">{it.transport.toUpperCase()}</Pill> },
        { name: "register", label: "Register", render: (it) => it.register ? <Pill tone="green">Yes</Pill> : <Pill tone="gray">No</Pill> },
        { name: "status", label: "Status", render: (it) => it.status === "active" ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Disabled</Pill> },
      ]}
      fields={[
        { name: "name", label: "Trunk Name", required: true, placeholder: "primary_sip" },
        { name: "provider", label: "Provider" },
        { name: "host", label: "SIP Host", required: true, placeholder: "sip.provider.com" },
        { name: "port", label: "Port", type: "number", default: 5060 },
        { name: "username", label: "Auth Username" },
        { name: "secret", label: "Auth Password" },
        { name: "from_user", label: "From User" },
        { name: "from_domain", label: "From Domain" },
        { name: "outbound_caller_id", label: "Outbound Caller ID" },
        { name: "max_channels", label: "Max Channels", type: "number", default: 30 },
        { name: "transport", label: "Transport", type: "select", options: transportOpts, default: "udp" },
        { name: "register", label: "Register with provider", type: "switch", default: true },
        { name: "codecs", label: "Codecs", type: "multiselect", options: CODECS, default: ["ulaw", "alaw", "g722"], full: true },
        { name: "status", label: "Status", type: "select", options: statusOpts, default: "active" },
      ]}
    />
  );
}

export function InboundRoutes() {
  return (
    <CrudPage
      title="Inbound Routes" testid="inbound" addLabel="New Inbound Route"
      subtitle="Map incoming DIDs to a destination — an extension, IVR, ring group, queue or department."
      endpoint="/inbound-routes"
      columns={[
        { name: "did_number", label: "DID / DDI", render: (it) => <span className="font-mono font-semibold text-[hsl(var(--gold))]">{it.did_number}</span> },
        { name: "description", label: "Description" },
        { name: "destination_type", label: "Destination", render: (it) => <Pill tone="blue">{it.destination_type}</Pill> },
        { name: "destination_id", label: "Target", render: (it) => <span className="font-mono text-xs text-muted-foreground">{it.destination_id || "—"}</span> },
      ]}
      fields={[
        { name: "did_number", label: "DID / DDI Number", required: true, placeholder: "+35544000000" },
        { name: "description", label: "Description" },
        { name: "destination_type", label: "Destination Type", type: "select", options: destTypeOpts, default: "extension" },
        { name: "destination_id", label: "Destination", type: "destination", dependsOn: "destination_type", full: true, help: "Pick the exact target this DID should ring." },
        { name: "cid_pattern", label: "Caller ID Filter (optional)" },
      ]}
    />
  );
}

export function OutboundRoutes() {
  return (
    <CrudPage
      title="Outbound Routes" testid="outbound" addLabel="New Outbound Route"
      subtitle="Decide which trunk carries outbound calls based on dialled patterns. Strip/prepend digits as needed."
      endpoint="/outbound-routes"
      columns={[
        { name: "name", label: "Name", render: (it) => <span className="font-medium">{it.name}</span> },
        { name: "dial_pattern", label: "Pattern", render: (it) => <span className="font-mono">{it.dial_pattern}</span> },
        { name: "trunk_id", label: "Trunk", render: (it, h) => h.labelForValue(h.fields.find((f) => f.name === "trunk_id"), it.trunk_id) },
        { name: "strip_digits", label: "Strip" },
        { name: "prepend", label: "Prepend", render: (it) => it.prepend || "—" },
        { name: "priority", label: "Priority" },
      ]}
      fields={[
        { name: "name", label: "Route Name", required: true },
        { name: "dial_pattern", label: "Dial Pattern", required: true, placeholder: "_00. or _1NXXNXXXXXX" },
        { name: "trunk_id", label: "Via Trunk", type: "select", optionsEndpoint: "/trunks", required: true },
        { name: "department_id", label: "Restrict to Department", type: "select", optionsEndpoint: "/departments" },
        { name: "strip_digits", label: "Strip Leading Digits", type: "number", default: 0 },
        { name: "prepend", label: "Prepend Digits" },
        { name: "caller_id", label: "Override Caller ID" },
        { name: "priority", label: "Priority", type: "number", default: 0 },
      ]}
    />
  );
}

export function RingGroups() {
  return (
    <CrudPage
      title="Ring Groups" testid="rg" addLabel="New Ring Group"
      subtitle="Ring several extensions at once (or in sequence). Great for small teams that share a number."
      endpoint="/ring-groups"
      columns={[
        { name: "name", label: "Name", render: (it) => <span className="font-medium">{it.name}</span> },
        { name: "extension", label: "Group Ext", render: (it) => <span className="font-mono text-[hsl(var(--gold))]">{it.extension}</span> },
        { name: "strategy", label: "Strategy", render: (it) => <Pill tone="blue">{it.strategy}</Pill> },
        { name: "members", label: "Members", render: (it) => `${(it.members || []).length} agents` },
        { name: "ring_time", label: "Ring (s)" },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "extension", label: "Group Extension", required: true, placeholder: "600" },
        { name: "strategy", label: "Ring Strategy", type: "select", default: "ringall",
          options: [{ value: "ringall", label: "Ring All" }, { value: "hunt", label: "Hunt (sequential)" }, { value: "memoryhunt", label: "Memory Hunt" }] },
        { name: "ring_time", label: "Ring Time (s)", type: "number", default: 25 },
        { name: "members", label: "Members", type: "multiselect", optionsEndpoint: "/extensions", optionLabel: "display_name", full: true },
        { name: "fallback_destination_type", label: "Fallback", type: "select", default: "voicemail", options: destTypeOpts },
        { name: "fallback_destination_id", label: "Fallback Target", type: "destination", dependsOn: "fallback_destination_type", full: true },
      ]}
    />
  );
}

export function Queues() {
  return (
    <CrudPage
      title="Call Queues" testid="queue" addLabel="New Queue"
      subtitle="Distribute inbound calls across agents with hold music, position announcements and overflow handling."
      endpoint="/queues"
      columns={[
        { name: "name", label: "Name", render: (it) => <span className="font-medium">{it.name}</span> },
        { name: "extension", label: "Queue Ext", render: (it) => <span className="font-mono text-[hsl(var(--gold))]">{it.extension}</span> },
        { name: "strategy", label: "Strategy", render: (it) => <Pill tone="blue">{it.strategy}</Pill> },
        { name: "agents", label: "Agents", render: (it) => `${(it.agents || []).length} agents` },
        { name: "max_wait", label: "Max Wait (s)" },
      ]}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "extension", label: "Queue Extension", required: true, placeholder: "700" },
        { name: "strategy", label: "Distribution", type: "select", default: "rrmemory",
          options: [
            { value: "rrmemory", label: "Round Robin (memory)" }, { value: "ringall", label: "Ring All" },
            { value: "leastrecent", label: "Least Recent" }, { value: "fewestcalls", label: "Fewest Calls" },
            { value: "random", label: "Random" }] },
        { name: "max_wait", label: "Max Wait (s)", type: "number", default: 300 },
        { name: "music_on_hold", label: "Music on Hold", default: "default" },
        { name: "announce_position", label: "Announce Position", type: "switch", default: true },
        { name: "agents", label: "Agents", type: "multiselect", optionsEndpoint: "/extensions", optionLabel: "display_name", full: true },
        { name: "fallback_destination_type", label: "Overflow To", type: "select", default: "voicemail", options: destTypeOpts },
        { name: "fallback_destination_id", label: "Overflow Target", type: "destination", dependsOn: "fallback_destination_type", full: true },
      ]}
    />
  );
}

export function Users() {
  return (
    <CrudPage
      title="Users" testid="user" addLabel="New User"
      subtitle="Console accounts. Admins manage the whole PBX; users get scoped access."
      endpoint="/users"
      columns={[
        { name: "name", label: "Name", render: (it) => <span className="font-medium">{it.name}</span> },
        { name: "email", label: "Email", render: (it) => <span className="text-muted-foreground">{it.email}</span> },
        { name: "role", label: "Role", render: (it) => it.role === "admin" ? <Pill tone="gold">Admin</Pill> : <Pill tone="gray">User</Pill> },
        { name: "status", label: "Status", render: (it) => it.status === "disabled" ? <Pill tone="red">Disabled</Pill> : <Pill tone="green">Active</Pill> },
      ]}
      fields={[
        { name: "name", label: "Full Name", required: true },
        { name: "email", label: "Email", required: true },
        { name: "password", label: "Password", type: "password", full: true, help: "Required for new users. Leave blank when editing to keep the current password." },
        { name: "role", label: "Role", type: "select", default: "user", options: [{ value: "admin", label: "Admin" }, { value: "user", label: "User" }] },
        { name: "department_id", label: "Department", type: "select", optionsEndpoint: "/departments" },
        { name: "status", label: "Status", type: "select", default: "active", options: statusOpts },
      ]}
    />
  );
}
